import { t } from '@lingui/core/macro'
import { ChevronDown, ChevronRight, ChevronUp } from '@untitled-ui/icons-react'
import { isBoolean } from 'lodash'
import {
  forwardRef,
  isValidElement,
  ReactElement,
  ReactNode,
  useState
} from 'react'
import {
  Button as RCAButton,
  MenuTrigger,
  Selection,
  SubmenuTrigger
} from 'react-aria-components'
import { twMerge } from 'tailwind-merge'
import Badge, { BadgeColors, BadgeSizes } from './Badge'
import { Button, ButtonProps } from './Button'
import { Description, Label } from './Field'
import { Menu, MenuItem } from './Menu'

export type DropdownMenuItem = {
  label: string
  description?: string
  id: string
  icon?: ReactNode
  badgeLabel?: string
  badgeColor?: BadgeColors
  children?: Array<DropdownMenuItem>
  onHover?: (hovered: boolean) => void
}

interface DropdownMenuProps
  extends Pick<ButtonProps, 'iconLeading' | 'hierarchy'> {
  btnLabel: string
  showCaret?: boolean
  onSelectOption: (selection: Selection) => void
  items: Array<DropdownMenuItem>
  customButton?: ReactNode | ((isOpen: boolean) => ReactElement)
  customButtonClassName?: string
  customMenuItem?: (item: DropdownMenuItem) => ReactElement
  customSubmenu?: (item: DropdownMenuItem) => ReactElement
  description?: string
  controlledIsOpen?: boolean
  loadingState?:
    | 'loading'
    | 'sorting'
    | 'loadingMore'
    | 'error'
    | 'idle'
    | 'filtering'
  setControlledIsOpen?: (isOpen: boolean) => void
  isNonModal?: boolean
  onClose?: () => void
  label?: string
  search?: string
  onSearch?: (search: string) => void
}

const DropdownMenu = forwardRef<HTMLDivElement, DropdownMenuProps>(
  (
    {
      items,
      iconLeading,
      hierarchy,
      btnLabel,
      showCaret = true,
      customButton,
      customButtonClassName,
      controlledIsOpen,
      setControlledIsOpen,
      onSelectOption,
      loadingState,
      customMenuItem,
      customSubmenu,
      description,
      isNonModal,
      onClose,
      label,
      search,
      onSearch
    }: DropdownMenuProps,
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [submenuGeneration, setSubmenuGeneration] = useState(0)

    const renderedCustomButton = customButton
      ? typeof customButton === 'function'
        ? customButton(isOpen)
        : customButton
      : null

    const customButtonIsAlreadyAButton =
      isValidElement(renderedCustomButton) &&
      (renderedCustomButton.type === 'button' ||
        renderedCustomButton.type === Button ||
        renderedCustomButton.type === RCAButton)

    const renderSubmenu = (item: DropdownMenuItem) => {
      if (item.children) {
        return (
          <SubmenuTrigger key={`${item.id}-${submenuGeneration}`}>
            {customMenuItem ? (
              customMenuItem(item)
            ) : (
              <MenuItem
                isSubmenu={true}
                aria-label={item.label}
                onHoverChange={item.onHover}
                onAction={() => item.onHover?.(true)}>
                <span className='flex w-full items-center justify-between'>
                  <span className='flex items-center'>
                    {item.icon && <span className='mr-2'>{item.icon}</span>}
                    <span>
                      {item.label}

                      {item.description && (
                        <p className={'text-caption'}>{item.description}</p>
                      )}
                    </span>
                  </span>
                  <ChevronRight width={16} />
                </span>
              </MenuItem>
            )}
            {customSubmenu ? (
              customSubmenu(item)
            ) : customMenuItem ? (
              customMenuItem(item)
            ) : (
              <Menu
                selectionMode={'single'}
                onSelectionChange={onSelectOption}
                items={item.children}>
                {renderSubmenu}
              </Menu>
            )}
          </SubmenuTrigger>
        )
      } else {
        return customMenuItem ? (
          customMenuItem(item)
        ) : (
          <MenuItem
            onHoverChange={(hovered: boolean) => {
              item.onHover?.(hovered)
            }}
            key={item.id}
            aria-label={item.label}>
            <span className='flex items-center'>
              {item.icon && <span className='mr-2'>{item.icon}</span>}
              <span>
                {item.label}

                {item.description && (
                  <p className={'text-caption'}>{item.description}</p>
                )}
              </span>

              {item.badgeLabel && (
                <div className={'ml-4'}>
                  <Badge size={BadgeSizes.s} color={item.badgeColor}>
                    {item.badgeLabel}
                  </Badge>
                </div>
              )}
            </span>
          </MenuItem>
        )
      }
    }

    return (
      <MenuTrigger
        isOpen={isBoolean(controlledIsOpen) ? controlledIsOpen : isOpen}
        onOpenChange={isOpen => {
          if (setControlledIsOpen) {
            setControlledIsOpen(isOpen)
          } else {
            setIsOpen(isOpen)
          }

          if (!isOpen) {
            setSubmenuGeneration(generation => generation + 1)
            onClose?.()
          }
        }}>
        <div className={'gap-1 flex flex-col'}>
          {label && (
            <Label className={'capitalize-first-letter'}>{label}</Label>
          )}
          {customButton ? (
            customButtonIsAlreadyAButton ? (
              renderedCustomButton
            ) : (
              <RCAButton
                className={twMerge(
                  'm-0 inline-flex self-start appearance-none items-center justify-start border-0 bg-transparent p-0 outline-none shadow-none',
                  customButtonClassName
                )}>
                {renderedCustomButton}
              </RCAButton>
            )
          ) : (
            <Button
              hierarchy={hierarchy}
              size={'small'}
              iconLeading={iconLeading}
              iconTrailing={
                !showCaret ? undefined : isOpen ? (
                  <ChevronUp width={16}></ChevronUp>
                ) : (
                  <ChevronDown width={16}></ChevronDown>
                )
              }
              label={btnLabel}
              className='px-2'></Button>
          )}
          {description && <Description>{description}</Description>}

          <Menu
            ref={ref}
            key={`root-menu-${submenuGeneration}`}
            search={search}
            onSearch={onSearch}
            isNonModal={isNonModal}
            selectionMode={'single'}
            onSelectionChange={onSelectOption}
            items={items}>
            {loadingState === 'loading' || loadingState === 'filtering' ? (
              <MenuItem key='loading' aria-label='Loading...'>
                <div
                  className={twMerge(
                    'flex w-full animate-pulse flex-col',
                    items.length === 0 ? 'gap-2' : ''
                  )}>
                  {items.length === 0 ? (
                    <>
                      <div className='h-8 bg-neutral-100 rounded'></div>
                      <div className='h-8 bg-neutral-100 rounded'></div>
                      <div className='h-8 bg-neutral-100 rounded'></div>
                    </>
                  ) : (
                    <div className='h-8 bg-neutral-100 rounded'></div>
                  )}
                </div>
              </MenuItem>
            ) : !['filtering', 'loading'].includes(loadingState ?? 'idle') &&
              items.length === 0 ? (
              <MenuItem
                key='loading'
                aria-label='empty state'
                isDisabled
                showCheck={false}>
                <p className='text-neutral-700 text-sm'>{t`No results found`}</p>
              </MenuItem>
            ) : (
              renderSubmenu
            )}
          </Menu>
        </div>
      </MenuTrigger>
    )
  }
)

DropdownMenu.displayName = 'DropdownMenu'

export default DropdownMenu
